import { signs, nakshatras, declinationAspectMap, fixedStars } from './timeEventTables';

/**
 * THE FOUR STRATEGY DESIGNERS' PAYLOADS, AS PURE FUNCTIONS.
 *
 * <h2>What this exists to fix</h2>
 *
 * Audit blocker 4: each designer assembled its request inline inside its own submit handler, so
 * a Command Bus could not run one without rendering the dialog, clicking through the form and
 * pressing the button. Extracting the assembly makes `strategy.run` possible and makes the
 * payloads testable — which they were not, since reaching them meant mounting a component that
 * needs auth, a chart and a backend.
 *
 * <h2>⚠ EXACT REPRODUCTIONS, and that is the whole safety argument</h2>
 *
 * Every one of these posts to a live endpoint that a working study depends on. A field renamed,
 * a fallback dropped or an index shifted would not fail — it would return a plausible answer to
 * a different question. So each builder reproduces its current inline construction field for
 * field, including the parts that look like oversights, and `strategyPayloadBuilders.test.js`
 * pins each against a literal expected object rather than against a second derivation.
 *
 * <p>The differences between them are preserved rather than harmonised. Designers 1 and 2 take
 * `startDateTime` from the RAW series and 3 takes it from the EXTENDED one; 1 and 2 fall back
 * through a chain and 3 does not. Those are three live studies' behaviour, not a style to tidy.
 *
 * <h2>Where each payload was assembled before</h2>
 *
 * <table>
 *   <tr><td>1 Strategy Design</td><td>inside the dialog</td></tr>
 *   <tr><td>2 Strategy 2</td><td>SPLIT — the dialog emitted part, `App` added the rest</td></tr>
 *   <tr><td>3 Time and Event</td><td>SPLIT — the dialog emitted part, `App` added the rest</td></tr>
 *   <tr><td>4 MOA</td><td>inside the dialog</td></tr>
 * </table>
 *
 * The two split ones are the reason this module takes `contextData` separately from `config`: a
 * dialog never knew the symbol or the date range, and a headless caller has to supply both.
 */

/** `designerId` → the endpoint its payload is posted to. */
export const STRATEGY_ENDPOINTS = {
  1: '/api/strategy-design',
  2: '/api/run-strategy2',
  3: '/api/run-time-event-strategy',
  4: '/api/moa-strategy',
};

/** Human names, for a refusal message that says which designer it meant. */
export const STRATEGY_NAMES = {
  1: 'Strategy Design',
  2: 'Strategy 2 Design',
  3: 'Time and Event Strategy',
  4: 'MOA Strategy',
};

const arr = (v) => (Array.isArray(v) ? v : []);
const last = (list) => (list.length > 0 ? list[list.length - 1] : undefined);

/**
 * The date range designers 1 and 2 send.
 *
 * <p>⚠ START from the RAW series and END from the EXTENDED one, falling back to the raw series'
 * own end. That asymmetry is in both of their current payloads and is preserved: the study is
 * computed from the first traded bar, and the end reaches into the padded columns so a forward
 * window can be returned. Designer 3 does NOT share it — see its own builder.
 */
function seriesRange({ barData, extendedBarData } = {}) {
  const raw = arr(barData?.dates);
  const extended = arr(extendedBarData?.dates);
  return {
    startDateTime: raw.length > 0 ? raw[0] : undefined,
    endDateTime: extended.length > 0 ? last(extended) : (raw.length > 0 ? last(raw) : undefined),
  };
}

/**
 * DESIGNER 1 — the score study.
 *
 * <p>The only transform is the type fix: a condition added as type `1` is sent as `'harmonic'`.
 * The dialog's own add-handler already writes `type: 'harmonic'` for that case, so the numeric
 * branch is a legacy shape — kept because dropping it would silently change a saved condition
 * that still carries it.
 *
 * @param config `{ studies, scoreThreshold, highlightColor }`
 * @param contextData `{ barData, extendedBarData }`
 */
export function buildStrategy1Payload(config = {}, contextData = {}) {
  const { startDateTime, endDateTime } = seriesRange(contextData);
  return {
    studies: arr(config.studies).map((study) => {
      const fixedType = study.type === 1 ? 'harmonic' : study.type;
      const { ...rest } = study;
      return { ...rest, type: fixedType };
    }),
    symbol: contextData.barData?.symbol,
    scoreThreshold: config.scoreThreshold,
    highlightColor: config.highlightColor,
    startDateTime,
    endDateTime,
  };
}

/**
 * DESIGNER 2 — the Vedic condition study.
 *
 * <p>⚠ The dialog emits `{studies, color, threshold}` and the payload wants
 * `{studies, highlightColor, scoreThreshold}`. The rename happened in `App`'s handler, which is
 * why it reads as two different vocabularies for one thing; this builder takes the PAYLOAD's
 * names and the caller adapts, so there is one place the mapping lives rather than two.
 *
 * <p>No `token` field: the dialog puts a refreshed id token on its emitted object, which is a
 * transport concern the interceptor already handles and was never part of the request body.
 */
export function buildStrategy2Payload(config = {}, contextData = {}) {
  const { startDateTime, endDateTime } = seriesRange(contextData);
  return {
    studies: arr(config.studies),
    scoreThreshold: config.scoreThreshold,
    highlightColor: config.highlightColor,
    symbol: contextData.barData?.symbol,
    startDateTime,
    endDateTime,
  };
}

/**
 * The per-study parameters for designer 3, which is the one real switch in this module.
 *
 * <p>Returns null for a study type it does not know, so the caller refuses rather than posting a
 * `params` object with nothing in it — which the backend would accept and answer emptily.
 */
function timeEventParams(studyType, f = {}) {
  switch (studyType) {
    case 'harmonic':
      return { planet: f.harmonic_planet, harmonic: f.harmonic, location: f.location };
    case 'planet_entering_sign':
      // 1-BASED, and read off the shared table. An entry inserted into the dropdown's array
      // would otherwise shift every index the backend receives, silently.
      return { planet: f.entering_sign_planet, sign: signs.indexOf(f.entering_sign) + 1 };
    case 'entering_nakshatra':
      return {
        planet: f.entering_nakshatra_planet,
        nakshatra: nakshatras.indexOf(f.entering_nakshatra) + 1,
      };
    case 'aspect':
      return { planet1: f.aspect_planet1, planet2: f.aspect_planet2, aspect: f.aspect };
    case 'declination_aspect':
      return {
        planet1: f.declination_planet1,
        planet2: f.declination_planet2,
        declination_aspect: declinationAspectMap[f.declination_aspect],
      };
    case 'zero_declination':
      return { planet: f.zero_declination_planet };
    case 'friendly_sign':
      return { planet: f.friendly_sign_planet };
    case 'inimical_sign':
      return { planet: f.inimical_sign_planet };
    case 'galactic_center':
      return { planet: f.galactic_center_planet };
    case 'rising':
      return { planet: f.rising_planet };
    case 'declination_more_than_sun':
      // The state field really is `setting_planet` for this study. Preserved verbatim; the
      // dialog carries the same note.
      return { planet: f.setting_planet };
    case 'solarOrLunarEclipse': {
      const params = { type: f.eclipse_type, trackTransits: !!f.eclipse_track_transits };
      // transitScope is sent ONLY when the extension is on -- the backend branches on its
      // presence, so sending it unconditionally would turn the extension on for everybody.
      if (f.eclipse_track_transits) params.transitScope = f.eclipse_transit_scope;
      return params;
    }
    case 'conjunct_star': {
      const params = { planet: f.conjunct_star_planet };
      const star = fixedStars.find(s => s.name === f.conjunct_star);
      // Absent rather than null when the star is unknown, matching the current `if (selectedStar)`.
      if (star) params.star_longitude = star.longitude;
      return params;
    }
    default:
      return null;
  }
}

/**
 * DESIGNER 3 — one study type at a time.
 *
 * <p>⚠ ITS DATE RANGE IS NOT THE OTHERS'. Both ends come from the EXTENDED series, with no
 * fallback to the raw one — which is what `App`'s handler does today. Designers 1 and 2 start
 * from the raw series and fall back; this one does neither. Three live studies, three
 * behaviours, all preserved.
 *
 * @param config `{ studyType, timeOption, formValues, colors }`
 * @returns the payload, or null when the study type is unknown
 */
export function buildTimeAndEventPayload(config = {}, contextData = {}) {
  const { studyType, timeOption, formValues = {}, colors = {} } = config;
  const params = timeEventParams(studyType, formValues);
  if (params === null) return null;

  const extended = arr(contextData.extendedBarData?.dates);
  return {
    studyType,
    timeOption,
    daysBefore: timeOption === 'before'
      ? formValues.time_before
      : (timeOption === 'around' ? formValues.time_around_before : 0),
    daysAfter: timeOption === 'after'
      ? formValues.time_after
      : (timeOption === 'around' ? formValues.time_around_after : 0),
    params: { ...params, color: colors[studyType] },
    symbol: contextData.barData?.symbol,
    startDateTime: extended[0],
    endDateTime: last(extended),
  };
}

/**
 * DESIGNER 4 — MOA.
 *
 * <p>⚠ IT SHIPS THE WHOLE PRICE SERIES. `priceDates` and `ohlc` are in the request body, so this
 * payload is megabytes where the other three are kilobytes. Preserved because the endpoint
 * expects it; worth knowing before calling it in a loop.
 *
 * <p>The source is the EXTENDED series when there is one, falling back to the raw — `||`, not
 * `??`, matching the current `extendedBarData || barData`.
 *
 * @param config `{ conditions, timeWindowMode, twNBefore, twNAfter, twNAround1, twNAround2,
 *                  selMode, exactVal, gteVal, lteVal, rangeGte, rangeLte, color }`
 * @returns the payload, or null when no bars are loaded
 */
export function buildMOAPayload(config = {}, contextData = {}) {
  const source = contextData.extendedBarData || contextData.barData;
  const dates = arr(source?.dates);
  const ohlc = arr(source?.ohlc);
  if (dates.length === 0) return null;

  const mode = config.timeWindowMode;
  const n1 = Number(mode === 'before' ? config.twNBefore
    : mode === 'after' ? config.twNAfter
      : config.twNAround1);
  const n2 = mode === 'around' ? Number(config.twNAround2) : n1;

  return {
    // The T is stripped: these dates reach the backend as plain days.
    startDate: String(dates[0]).split('T')[0],
    endDate: String(last(dates)).split('T')[0],
    conditions: arr(config.conditions),
    timeWindow: { mode, n1, n2 },
    selection: {
      mode: config.selMode,
      exactVal: Number(config.exactVal),
      gteVal: Number(config.gteVal),
      lteVal: Number(config.lteVal),
      rangeGte: Number(config.rangeGte),
      rangeLte: Number(config.rangeLte),
    },
    color: config.color,
    priceDates: dates,
    ohlc,
  };
}

/** The four builders by designer id, so a caller never writes the mapping itself. */
export const STRATEGY_BUILDERS = {
  1: buildStrategy1Payload,
  2: buildStrategy2Payload,
  3: buildTimeAndEventPayload,
  4: buildMOAPayload,
};

/** How long a designer-3 run may poll before giving up, and how often. */
export const TIME_EVENT_POLL_MS = 1000;
export const TIME_EVENT_TIMEOUT_MS = 180000;

const sleep = (ms) => new Promise(resolve => { setTimeout(resolve, ms); });

/**
 * ⚠ DESIGNER 3 IS SUBMIT-THEN-POLL, and the other three are not.
 *
 * <p>`/api/run-time-event-strategy` answers with a `taskId` and the result is collected from
 * `/result/{taskId}`, which returns a non-200 until the study finishes — `spring.mvc.async
 * .request-timeout` is 180000, so the ceiling here matches it. A headless executor that returned
 * `response.data` for this one would hand its caller `{taskId}` and call it a result.
 *
 * <p>The timeout rejects rather than resolving with nothing: a strategy run that silently
 * produced no trades is a finding, and one that silently gave up is a bug wearing its clothes.
 */
async function pollTimeEventResult(api, taskId, { pollMs = TIME_EVENT_POLL_MS,
  timeoutMs = TIME_EVENT_TIMEOUT_MS } = {}) {
  const deadline = Date.now() + timeoutMs;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const response = await api.get(`${STRATEGY_ENDPOINTS[3]}/result/${taskId}`);
    if (response && response.status === 200) return response.data;
    if (Date.now() >= deadline) {
      throw new Error(
        `Time and Event Strategy did not finish within ${timeoutMs}ms (task ${taskId}).`);
    }
    // eslint-disable-next-line no-await-in-loop
    await sleep(pollMs);
  }
}

/**
 * RUN ONE DESIGNER WITHOUT ITS DIALOG.
 *
 * <p>Builds the payload, posts it, and returns what the backend answered. It does NOT paint the
 * chart or open the messages panel — those are the dialogs' own follow-through and they need
 * `addOverlay`, the journal and half of `App`. Keeping them out is what makes this callable from
 * a script, a test or a Command Bus; a caller that wants the chart drawn should open the dialog.
 *
 * @param api         the authenticated axios instance
 * @param designerId  1 | 2 | 3 | 4
 * @param config      that designer's form state
 * @param contextData `{ barData, extendedBarData }`
 * @returns the backend's result object
 * @throws when the designer id is unknown, the payload cannot be built, or the run fails
 */
export async function runStrategyHeadless(api, designerId, config, contextData = {}, options = {}) {
  const id = Number(designerId);
  const build = STRATEGY_BUILDERS[id];
  const endpoint = STRATEGY_ENDPOINTS[id];
  if (!build || !endpoint) {
    throw new Error(
      `Unknown strategy designer "${designerId}". Expected 1, 2, 3 or 4.`);
  }
  if (!api || typeof api.post !== 'function') {
    throw new Error('runStrategyHeadless needs an authenticated api client.');
  }

  const payload = build(config, contextData);
  if (payload === null) {
    // Named per designer, because the two that can refuse do so for different reasons: an
    // unknown study type, and no bars loaded.
    throw new Error(
      `${STRATEGY_NAMES[id]}: the configuration did not produce a payload. `
      + (id === 3 ? 'Unknown studyType.' : 'No price series was supplied.'));
  }

  const response = await api.post(endpoint, payload);
  if (id !== 3) return response.data;

  const taskId = response.data?.taskId;
  if (!taskId) {
    throw new Error('Time and Event Strategy returned no taskId to poll.');
  }
  return pollTimeEventResult(api, taskId, options);
}
